import React, { useEffect, useRef, useState, useMemo } from "react";
import { useLocation, useNavigate, useParams, Link } from "react-router-dom";
import axios from "axios";
import { Breadcrumb, Modal, Progress, Tooltip, message, Button, Slider, Switch } from "antd";
import { useCookies } from "react-cookie";
import dayjsInstance from "../utils/dayjs";
import SpeechRecognition, { useSpeechRecognition } from 'react-speech-recognition';
import {
    HomeOutlined,
    StepBackwardOutlined, StepForwardOutlined,
    TrophyOutlined, FireOutlined, CheckCircleOutlined, VideoCameraOutlined, AudioOutlined,
    LoadingOutlined, WarningOutlined, PlayCircleFilled, PauseCircleFilled,
    DownloadOutlined, SettingOutlined, FontSizeOutlined,
    EyeOutlined, RetweetOutlined, SyncOutlined, FlagOutlined, StarOutlined, ClockCircleOutlined, CommentOutlined
} from "@ant-design/icons";
import toCamelCase from "../utils/toCamelCase";
import toLowerCamelCase from "../utils/toLowerCamelCase";
import { alignTranscript } from "../utils/alignTranscript";
import convertTimeToMilliseconds from "../utils/convertTimeToMilliseconds";
import { textToIPAPure } from "../common/ipaPure";

// Waveform rendering constants — single source of truth for colors/thresholds used by drawWaveform()
const WAVE_GATE = 0.02; // amplitude below this is treated as silence
const WAVE_COLORS = {
    bg: '#232b27',
    bgOutside: '#171c1a',
    barActive: '#8dd0ab',
    barActiveStroke: '#5fa07d',
    barInactive: '#4a5a52',
    refBar: '#3d5a4b',
    refBarOutside: '#2a3631',
    silenceLine: '#8aa397',
    startMarker: '#f5c518',
    endMarker: '#ef5b61',
    label: '#ef5b61',
};
// Maps a raw smoothed envelope value (0..1) to a display amplitude (0..1): gated, then
// compressed so small sounds stay visible and large ones don't clip the bar height too early.
const waveAmp = (v) => (v < WAVE_GATE ? 0 : Math.min(1, Math.pow(v * 3, 0.7)));

// Sentence timeline: one waveform sample every WAVE_STEP_MS. The axis of a sentence is
// [RUN_LEAD_MS of run-up] + [the sentence's own time slot] + [RUN_TAIL_MS], and the reference
// audio's speech sits in the middle of it. Recording starts when the playhead reaches the start
// of that speech and ends (plus RUN_GRACE_MS) when it leaves it.
const WAVE_STEP_MS = 30;
const RUN_LEAD_MS = 700;
const RUN_TAIL_MS = 500;
const RUN_GRACE_MS = 350;
const SR_PREROLL_MS = 300; // start speech recognition slightly before the recording window
const REF_PEAK = 0.33; // reference envelope is normalised so its loudest peak maps to ~full bar height

// Attack-fast / release-slow follower so each syllable draws as one smooth bump.
const smoothEnvelope = (raw) => {
    const out = new Array(raw.length);
    let s = 0;
    for (let i = 0; i < raw.length; i++) {
        s += (raw[i] > s ? 0.6 : 0.15) * (raw[i] - s);
        out[i] = s;
    }
    return out;
};

// Peak amplitude of the whole lesson audio, one value per WAVE_STEP_MS. Decoded at 8kHz so even a
// long track stays small in memory. Resolves null if the audio can't be fetched/decoded.
const trackEnvelopeCache = {};
const getTrackEnvelope = (src) => {
    if (!src) return Promise.resolve(null);
    if (trackEnvelopeCache[src]) return trackEnvelopeCache[src];
    const promise = (async () => {
        try {
            const resp = await fetch(src);
            if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
            const data = await resp.arrayBuffer();
            const OfflineCtx = window.OfflineAudioContext || window.webkitOfflineAudioContext;
            const decoder = new OfflineCtx(1, 1, 8000);
            const audioBuf = await new Promise((resolve, reject) => decoder.decodeAudioData(data, resolve, reject));
            const step = Math.max(1, Math.round(audioBuf.sampleRate * WAVE_STEP_MS / 1000));
            const channels = [];
            for (let c = 0; c < audioBuf.numberOfChannels; c++) channels.push(audioBuf.getChannelData(c));
            const env = new Float32Array(Math.ceil(audioBuf.length / step));
            for (let k = 0; k < env.length; k++) {
                const to = Math.min(audioBuf.length, (k + 1) * step);
                let m = 0;
                for (const ch of channels) {
                    for (let j = k * step; j < to; j++) { const a = Math.abs(ch[j]); if (a > m) m = a; }
                }
                env[k] = m;
            }
            return env;
        } catch (e) {
            console.warn('Reference waveform unavailable (will show the sentence timeline without it):', e && e.message);
            delete trackEnvelopeCache[src];
            return null;
        }
    })();
    trackEnvelopeCache[src] = promise;
    return promise;
};

// Every recording of a sentence is uploaded to R2 and the sentence's record keeps the list of urls
// (newest first, JSON) in its `audio` column; older rows hold a single plain url. The upload time is in the file name.
const MAX_TAKES = 6;
const parseTakeUrls = (audio) => {
    if (!audio || typeof audio !== 'string') return [];
    const t = audio.trim();
    if (t.startsWith('[')) {
        try { const a = JSON.parse(t); return Array.isArray(a) ? a.filter(u => typeof u === 'string') : []; } catch (e) { return []; }
    }
    return t ? [t] : [];
};
const serializeTakeUrls = (urls) => {
    const list = urls.slice(0, MAX_TAKES);
    while (list.length > 1 && JSON.stringify(list).length > 1000) list.pop(); // the audio column is varchar(1024)
    return JSON.stringify(list);
};
const takeDate = (url) => {
    let decoded = url || '';
    try { decoded = decodeURIComponent(decoded); } catch (e) { }
    const m = /_(\d{13})\.[a-z0-9]+(\?.*)?$/i.exec(decoded);
    return m ? new Date(+m[1]) : null;
};
const formatTakeDate = (d) => {
    if (!d || isNaN(d.getTime())) return 'Không rõ ngày';
    const p = (n) => String(n).padStart(2, '0');
    return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`;
};

// Timeline for one sentence. `env` (optional) is the reference audio's amplitude per step across the
// sentence's own slot; the speech region is where it rises above the noise floor.
const buildSentenceSlot = (index, startMs, durMs, env) => {
    const totalMs = RUN_LEAD_MS + durMs + RUN_TAIL_MS;
    const slot = {
        index, startMs, durMs, totalMs,
        env: null,
        regionStartMs: RUN_LEAD_MS,
        regionEndMs: RUN_LEAD_MS + durMs,
        hasRef: false,
    };
    if (env && env.length) {
        let maxP = 0;
        for (let i = 0; i < env.length; i++) if (env[i] > maxP) maxP = env[i];
        if (maxP > 0.003) {
            const normalised = Array.from(env, v => v * (REF_PEAK / maxP));
            const thr = Math.max(WAVE_GATE, 0.08 * REF_PEAK);
            // The region is found on the raw values (the smoothed ones trail off slowly after the voice stops)
            let first = -1, last = -1;
            for (let i = 0; i < normalised.length; i++) { if (normalised[i] >= thr) { if (first < 0) first = i; last = i; } }
            if (first >= 0) {
                first = Math.max(0, first - 1);
                last = Math.min(normalised.length - 1, last + 3);
                slot.env = smoothEnvelope(normalised);
                slot.regionStartMs = RUN_LEAD_MS + first * WAVE_STEP_MS;
                slot.regionEndMs = RUN_LEAD_MS + (last + 1) * WAVE_STEP_MS;
                slot.hasRef = true;
            }
        }
    }
    return slot;
};

export default function Speaking() {
    const { slug } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const index = location?.state?.index ?? 0;
    const containerRef = useRef(null);

    const [dataLesson, setdataLesson] = useState([]);
    const [options, setOptions] = useState([]);
    const [records, setRecords] = useState([]);
    const [recordMain, setRecordMain] = useState([]);
    const [arrayMain, setArrayMain] = useState([]);
    const [indexLyric, setIndexLyric] = useState(0);
    const [recordingIndex, setRecordingIndex] = useState(-1);
    const [story, setStory] = useState("Main Story");
    const [recordingAll, setRecordingAll] = useState(false);
    const [isScoringAll, setIsScoringAll] = useState(false); // true while batch-scoring after stop
    const [isModalSpeakOpen, setIsModalSpeakOpen] = useState(false);
    const [readingSpeed, setReadingSpeed] = useState(1); // multiplier: 0.5 = slow, 1 = normal, 1.5 = fast
    const [dataUserTopic, setDataUserTopic] = useState({});
    const [cookies] = useCookies(["user"]);
    const [micPermission, setMicPermission] = useState('unknown'); // 'unknown' | 'granted' | 'denied' | 'unsupported'
    const [timestamps, setTimestamps] = useState({});     // LRC timestamps per track
    const [audioCurrentTime, setAudioCurrentTime] = useState(0); // ms
    const [audioIsPlaying, setAudioIsPlaying] = useState(false);
    const [runIndex, setRunIndex] = useState(-1); // sentence whose timeline is running (run-up + recording); -1 = idle
    const [showScoreModal, setShowScoreModal] = useState(false);
    const [scoreResult, setScoreResult] = useState(null); // { avgPoint, lines, totalLines }
    const [showSettings, setShowSettings] = useState(false);
    // For special handling of bai-01-day-of-the-dead: session-only attempt counter
    const [attemptCount, setAttemptCount] = useState(0);
    const [attemptTarget, setAttemptTarget] = useState(3);
    const [subtitleFontSize, setSubtitleFontSize] = useState(16);
    const [translationFontSize, setTranslationFontSize] = useState(14);
    const [showTranslation, setShowTranslation] = useState(true);
    const [showIPA, setShowIPA] = useState(true);
    const [autoScroll, setAutoScroll] = useState(true);
    const [userInfo, setUserInfo] = useState(null); // fresh user data from API (vip_expire_at)
    const [fullRecordingUrl, setFullRecordingUrl] = useState(null); // R2 url of the whole-lesson recording, once all sentences are recorded
    const [isMergingRecording, setIsMergingRecording] = useState(false);
    const [lastResult, setLastResult] = useState(null); // { position, point, text } — the score shown right after the learner finishes speaking
    const [savingTake, setSavingTake] = useState(false); // uploading the recording / saving the score
    const [playingTakeKey, setPlayingTakeKey] = useState(null); // which saved recording is playing on the left
    const takeAudioRef = useRef(null);

    const [translations, setTranslations] = useState([]);
    const [ipaData, setIpaData] = useState([]);
    const [translationLoading, setTranslationLoading] = useState(false);

    // Live (while-recording) UI for single-sentence mode
    const elapsedLabelRef = useRef(null); // DOM node for the live "X.Xs" label — updated directly, not via React state, so it doesn't re-render the whole page 60x/sec

    // Determine whether this lesson should use the special UI/behavior
    const lessonHasSpecialUi = () => {
        try {
            return !!(
                dataLesson?.specialUi || dataLesson?.special_ui ||
                dataLesson?.behaviorType === 'special'
            );
        } catch (e) { return false; }
    };

    // Hidden audio element for camera-recording mode
    const audioElemRef = useRef(null);

    const { transcript, interimTranscript, listening, resetTranscript } = useSpeechRecognition();

    const getRecordRank = (rec) => {
        if (!rec) return -Infinity;
        const id = Number(rec.id);
        if (!Number.isNaN(id) && Number.isFinite(id)) return id;
        const t = Date.parse(rec.create_at || rec.created_at || rec.updated_at || '');
        if (!Number.isNaN(t) && Number.isFinite(t)) return t;
        return 0;
    };

    const dedupeLatestRecordsByPosition = (list) => {
        if (!Array.isArray(list) || list.length === 0) return [];
        const map = new Map();
        for (const r of list) {
            const pos = Number(r.position);
            const existing = map.get(pos);
            if (!existing || getRecordRank(r) > getRecordRank(existing)) {
                map.set(pos, r);
            }
        }
        return Array.from(map.values()).sort((a, b) => Number(a.position) - Number(b.position));
    };

    // Reset transient UI state to a clean start for this session
    const resetUI = () => {
        // Full page reload to restore initial mount state (ensures all hooks and data reinitialize)
        try {
            window.location.reload();
        } catch (e) {
            // Fallback to best-effort state reset
            setAttemptCount(0);
            setIndexLyric(0);
            setRecordingAll(false);
            setRecordingIndex(-1);
            setIsScoringAll(false);
            setScoreResult(null);
            setShowScoreModal(false);
            setAudioIsPlaying(false);
            resetTranscript();
            try { if (containerRef?.current) containerRef.current.scrollTop = 0; } catch (err) { }
        }
    };

    // Check browser support and request mic permission on mount
    useEffect(() => {
        if (!SpeechRecognition.browserSupportsSpeechRecognition()) {
            setMicPermission('unsupported');
            return;
        }
        // Request mic permission eagerly so user sees the prompt right away
        navigator.mediaDevices?.getUserMedia({ audio: true })
            .then(stream => {
                stream.getTracks().forEach(t => t.stop()); // release immediately
                setMicPermission('granted');
            })
            .catch(err => {
                if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
                    setMicPermission('denied');
                    message.error('Microphone bị từ chối. Vào Cài đặt trình duyệt → Quyền riêng tư → Microphone → Cho phép rồi tải lại trang.');
                } else {
                    setMicPermission('granted'); // device may still work
                }
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Refs so the listening-change effect can always read latest values
    // without those values being in the dependency array (which would re-fire the effect)
    const recordingAllRef = useRef(recordingAll);
    const fullTranscriptRef = useRef(''); // accumulate per-sentence transcripts during recordingAll
    const lastTranscriptRef = useRef(''); // remember last seen transcript to append deltas
    const sentenceTranscriptRef = useRef(''); // accumulate transcript for current per-sentence recording
    const sentenceLastTranscriptRef = useRef(''); // remember last seen transcript for per-sentence
    const indexLyricRef = useRef(indexLyric);
    const arrayMainRef = useRef(arrayMain);
    const transcriptRef = useRef(transcript);
    const dataLessonRef = useRef(dataLesson);
    const storyRef = useRef(story);
    const clickMicRef = useRef(null); // filled after clickMic is defined
    const userStoppedRef = useRef(false); // true when user manually pressed stop (no auto-advance)
    const hasStartedRef = useRef(false); // true once SpeechRecognition has started at least once
    const autoAdvanceTimerRef = useRef(null); // holds the setTimeout id for lyric auto-advance
    const timestampsRef = useRef({});
    const perSentenceMediaRecorderRef = useRef(null);
    const perSentenceChunksRef = useRef([]);
    const sentenceAudioTokenRef = useRef(0); // invalidates a stale playSentenceAudio() call when a newer one starts
    const micStreamForRecordingRef = useRef(null); // raw mic stream for the audio recorder (separate from SpeechRecognition's own mic access)
    const recorderAudioCtxRef = useRef(null);
    const analyserRef = useRef(null);
    const waveCanvasRef = useRef(null);
    const waveAnimFrameRef = useRef(null);
    const sentenceRecordingsRef = useRef({}); // position -> recorded audio Blob, for later merging into one full-lesson file
    const liveWavePeaksRef = useRef([]); // user's smoothed mic amplitude for the run in progress, one value per WAVE_STEP_MS of the sentence axis
    const smoothRef = useRef(0); // envelope-follower state (attack/release) for the current run
    const sentenceWavePeaksRef = useRef({}); // position -> user's amplitude array on the sentence axis, kept after recording for the "recorded" view
    const slotRef = useRef(null); // timeline of the current sentence (see buildSentenceSlot)
    const runRef = useRef({ active: false }); // the timeline run: playhead sweeping the sentence, recording inside the speech region
    const playheadMsRef = useRef(0); // playhead position on the axis while no run is active
    const barInputRef = useRef(null); // per-sentence timeline slider (updated imperatively, never via per-frame state)
    const barElapsedRef = useRef(null);
    const barTotalRef = useRef(null);
    const startRunRef = useRef(null);
    const finishRecordingRef = useRef(null);
    const readingSpeedRef = useRef(readingSpeed);
    useEffect(() => { readingSpeedRef.current = readingSpeed; }, [readingSpeed]);
    useEffect(() => { timestampsRef.current = timestamps; }, [timestamps]);
    // Release the mic/AudioContext/rAF loop if the user navigates away mid-recording
    useEffect(() => {
        return () => {
            runRef.current.active = false;
            try { if (waveAnimFrameRef.current) cancelAnimationFrame(waveAnimFrameRef.current); } catch (e) { }
            try { micStreamForRecordingRef.current?.getTracks().forEach(t => t.stop()); } catch (e) { }
            try { recorderAudioCtxRef.current?.close(); } catch (e) { }
        };
    }, []);

    // A different story/exercise reuses the same sentence positions — clear the cached finished
    // waveforms so an old recording's shape can't be shown under the wrong sentence.
    useEffect(() => {
        sentenceWavePeaksRef.current = {};
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [story]);

    // Fetch translations when toggled on
    useEffect(() => {
        if (showTranslation && arrayMain?.length > 0 && translations.length === 0) {
            fetchTranslations();
        }
    }, [showTranslation, arrayMain]);

    // Generate IPA when toggled on (try dynamic import of ipa util first)
    useEffect(() => {
        if (showIPA && arrayMain?.length > 0 && ipaData.length === 0) {
            (async () => {
                try {
                    const apiBase = (process.env.REACT_APP_API_URL || '');
                    const resp = await axios.post(`${apiBase.replace(/\/$/, '')}/api/ipa/batch`, { lines: arrayMain }, { timeout: 20000 });
                    const arr = (resp && resp.data && Array.isArray(resp.data.ipa)) ? resp.data.ipa : null;
                    if (arr) {
                        setIpaData(arr);
                        return;
                    }
                } catch (e) {
                    console.warn('IPA backend request failed, falling back to local:', e && e.message);
                }
                try { setIpaData(arrayMain.map(line => textToIPAPure(line || ''))); } catch (e) { setIpaData(arrayMain.map(() => '')); }
            })();
        }
    }, [showIPA, arrayMain]);

    const fetchTranslations = async () => {
        if (!arrayMain || arrayMain.length === 0) return;
        setTranslationLoading(true);
        try {
            const payload = { lines: arrayMain, from: 'en', to: 'vi' };
            const result = await axios.post(`${process.env.REACT_APP_API_URL}/translation/batch`, payload, { timeout: 30000 });

            if (result?.data?.translations && Array.isArray(result.data.translations) && result.data.translations.length === arrayMain.length) {
                const backendArr = result.data.translations;
                const hasNonEmpty = backendArr.some(t => typeof t === 'string' && t.trim().length > 0);
                if (hasNonEmpty) {
                    setTranslations(backendArr);
                } else {
                    // fallback to per-line LibreTranslate when backend returns empty strings
                    try {
                        const perLine = await Promise.all(arrayMain.map(async (ln) => {
                            try {
                                const r = await axios.post('https://libretranslate.de/translate', { q: ln || '', source: 'en', target: 'vi', format: 'text' }, { headers: { 'accept': 'application/json', 'Content-Type': 'application/json' }, timeout: 20000 });
                                return r.data?.translatedText || '';
                            } catch (inner) { console.error('Libre fallback line error', inner); return ''; }
                        }));
                        setTranslations(perLine);
                    } catch (inner) { console.error('Libre fallback failed', inner); setTranslations([]); }
                }
            } else {
                // backend returned invalid structure or length mismatch → try LibreTranslate per-line
                try {
                    const perLine = await Promise.all(arrayMain.map(async (ln) => {
                        try {
                            const r = await axios.post('https://libretranslate.de/translate', { q: ln || '', source: 'en', target: 'vi', format: 'text' }, { headers: { 'accept': 'application/json', 'Content-Type': 'application/json' }, timeout: 20000 });
                            return r.data?.translatedText || '';
                        } catch (inner) { console.error('Libre fallback line error', inner); return ''; }
                    }));
                    setTranslations(perLine);
                } catch (inner) { console.error('Libre fallback failed after invalid backend response', inner); setTranslations([]); }
            }
        } catch (e) {
            console.error('Translation error:', e);
            setTranslations([]);
            // attempt fallback
            try {
                const perLine = await Promise.all(arrayMain.map(async (ln) => {
                    try {
                        const r = await axios.post('https://libretranslate.de/translate', { q: ln || '', source: 'en', target: 'vi', format: 'text' }, { headers: { 'accept': 'application/json', 'Content-Type': 'application/json' }, timeout: 20000 });
                        return r.data?.translatedText || '';
                    } catch (inner) { console.error('Libre fallback line error', inner); return ''; }
                }));
                setTranslations(perLine);
            } catch (libErr) { console.error('LibreTranslate fallback failed:', libErr); }
        } finally {
            setTranslationLoading(false);
        }
    };

    // Use shared lightweight IPA converter from common/ipaPure

    useEffect(() => { recordingAllRef.current = recordingAll; }, [recordingAll]);
    useEffect(() => { indexLyricRef.current = indexLyric; }, [indexLyric]);
    useEffect(() => { arrayMainRef.current = arrayMain; }, [arrayMain]);
    useEffect(() => { transcriptRef.current = transcript; }, [transcript]);
    useEffect(() => { dataLessonRef.current = dataLesson; }, [dataLesson]);
    useEffect(() => { storyRef.current = story; }, [story]);

    // Sentence/story/timestamps changed: rebuild that sentence's timeline (and load the reference
    // waveform for it), and abandon any run that was still going for another sentence.
    // (Declared after the ref-sync effects above so prepareSlot sees the current lines/story/lesson.)
    useEffect(() => {
        if (runRef.current.active && runRef.current.index !== indexLyric) cancelRun();
        playheadMsRef.current = 0;
        prepareSlot(indexLyric);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [indexLyric, story, timestamps, arrayMain, dataLesson]);

    // Reference audio started playing: keep the playhead moving along the sentence's timeline
    useEffect(() => {
        if (audioIsPlaying) startWaveLoop();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [audioIsPlaying]);

    const convertString = (track) => {
        if (track && typeof track === 'string') {
            const data = track.split('\n').map(el => {
                const closeBracketIndex = el.indexOf(']');
                return closeBracketIndex >= 0 ? el.slice(closeBracketIndex + 1) : el;
            }).filter(line => line.trim()); // Remove empty lines
            setArrayMain(data);
        } else {
            setArrayMain([]);
        }
    };

    // ── Sentence timeline: reference waveform in the middle, playhead sweeping, recording inside the speech region ──
    // Everything below only reads refs/module constants (never React state) so the rAF loop that drives it can't go stale.

    /** Where the yellow playhead is, in ms on the sentence axis. */
    const getPlayheadMs = () => {
        const run = runRef.current;
        const slot = run.active ? run.slot : slotRef.current;
        if (!slot) return 0;
        if (run.active) return Math.min(slot.totalMs, Math.max(0, performance.now() - run.startTs));
        const audioEl = audioElemRef.current;
        if (audioEl && !audioEl.paused && !audioEl.ended && slot.startMs !== null) {
            return Math.min(slot.totalMs, Math.max(0, RUN_LEAD_MS + (audioEl.currentTime * 1000 - slot.startMs)));
        }
        return Math.min(slot.totalMs, Math.max(0, playheadMsRef.current));
    };

    /** Mirrors the playhead on the per-sentence timeline slider above the buttons (DOM only, no React state). */
    const updateBarDom = (playheadMs) => {
        const slot = runRef.current.active ? runRef.current.slot : slotRef.current;
        const input = barInputRef.current;
        if (!slot || !input) return;
        const totalSec = slot.totalMs / 1000;
        const sec = Math.min(totalSec, Math.max(0, playheadMs / 1000));
        input.max = String(totalSec);
        input.value = String(sec);
        const pct = totalSec > 0 ? (sec / totalSec) * 100 : 0;
        input.style.background = `linear-gradient(to right, #3b82f6 ${pct}%, rgba(255,255,255,0.1) ${pct}%)`;
        if (barElapsedRef.current) barElapsedRef.current.textContent = `${sec.toFixed(1)}s`;
        if (barTotalRef.current) barTotalRef.current.textContent = `${totalSec.toFixed(1)}s`;
    };

    /** Draws the sentence timeline: the reference audio's waveform, the user's own voice on top of it,
     * the speech region (where recording happens) and the yellow playhead. */
    const drawWaveform = (playheadArg) => {
        const canvas = waveCanvasRef.current;
        if (!canvas) return;
        const dpr = window.devicePixelRatio || 1;
        const cssWidth = canvas.clientWidth || 600;
        const cssHeight = canvas.clientHeight || 64;
        const pxWidth = Math.max(1, Math.round(cssWidth * dpr));
        const pxHeight = Math.max(1, Math.round(cssHeight * dpr));
        if (canvas.width !== pxWidth) canvas.width = pxWidth;
        if (canvas.height !== pxHeight) canvas.height = pxHeight;
        const ctx = canvas.getContext('2d');
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        const width = cssWidth;
        const height = cssHeight;
        const midY = height / 2;
        const run = runRef.current;
        const slot = run.active ? run.slot : slotRef.current;

        ctx.clearRect(0, 0, width, height);
        ctx.fillStyle = WAVE_COLORS.bg;
        ctx.fillRect(0, 0, width, height);

        const pxPerMs = slot ? width / slot.totalMs : 0;
        const xRs = slot ? slot.regionStartMs * pxPerMs : 0;
        const xRe = slot ? slot.regionEndMs * pxPerMs : width;

        // Outside the speech region is darker
        if (slot) {
            ctx.fillStyle = WAVE_COLORS.bgOutside;
            ctx.fillRect(0, 0, xRs, height);
            ctx.fillRect(xRe, 0, Math.max(0, width - xRe), height);
        }

        // Dashed baseline across the full width; bars are drawn over it, so silence stays a dashed line
        ctx.strokeStyle = WAVE_COLORS.silenceLine;
        ctx.lineWidth = 1;
        ctx.setLineDash([2, 3]);
        ctx.beginPath();
        ctx.moveTo(0, midY);
        ctx.lineTo(width, midY);
        ctx.stroke();
        ctx.setLineDash([]);
        if (!slot) return;

        const stepPx = WAVE_STEP_MS * pxPerMs;
        const barW = Math.max(1, stepPx - 1);
        const barMaxH = height * 0.42;

        // Reference audio (the target shape the user speaks along to)
        if (slot.env) {
            for (let j = 0; j < slot.env.length; j++) {
                const a = waveAmp(slot.env[j]);
                if (a <= 0) continue;
                const x = (RUN_LEAD_MS + j * WAVE_STEP_MS) * pxPerMs;
                const h = Math.max(2, a * barMaxH);
                ctx.fillStyle = (x >= xRs - 0.5 && x < xRe) ? WAVE_COLORS.refBar : WAVE_COLORS.refBarOutside;
                ctx.fillRect(x, midY - h, barW, h * 2);
            }
        }

        // The user's own voice (live while running, then what was recorded)
        const user = run.active ? liveWavePeaksRef.current : (sentenceWavePeaksRef.current[slot.index] || null);
        let userSpoke = false;
        if (user) {
            for (let k = 0; k < user.length; k++) {
                const a = waveAmp(user[k]);
                if (a <= 0) continue;
                const t = k * WAVE_STEP_MS;
                const inside = t >= slot.regionStartMs && t < slot.regionEndMs + RUN_GRACE_MS;
                const x = k * stepPx;
                const h = Math.max(2, a * barMaxH);
                ctx.fillStyle = inside ? WAVE_COLORS.barActive : WAVE_COLORS.barInactive;
                ctx.fillRect(x, midY - h, barW, h * 2);
                if (inside && barW >= 3) {
                    ctx.strokeStyle = WAVE_COLORS.barActiveStroke;
                    ctx.lineWidth = 1;
                    ctx.strokeRect(x + 0.5, midY - h + 0.5, barW - 1, h * 2 - 1);
                }
                if (inside) userSpoke = true;
            }
        }

        // Speech-region markers: dashed red at the start and end, labelled "0.0" and the region length
        ctx.strokeStyle = WAVE_COLORS.endMarker;
        ctx.lineWidth = 1.5;
        ctx.setLineDash([4, 4]);
        ctx.beginPath();
        ctx.moveTo(xRs, 0); ctx.lineTo(xRs, height);
        ctx.moveTo(xRe, 0); ctx.lineTo(xRe, height);
        ctx.stroke();
        ctx.setLineDash([]);
        ctx.fillStyle = WAVE_COLORS.label;
        ctx.font = '12px monospace';
        ctx.textBaseline = 'top';
        ctx.fillText('0.0', xRs + 4, 2);
        const durLabel = `${((slot.regionEndMs - slot.regionStartMs) / 1000).toFixed(1)}s`;
        ctx.fillText(durLabel, Math.max(0, xRe - ctx.measureText(durLabel).width - 4), 2);

        if (!run.active && user && user.length > 0 && !userSpoke) {
            ctx.textBaseline = 'bottom';
            ctx.fillText('Không nghe thấy tiếng nói. Thử nói to hơn.', Math.min(xRs + 6, Math.max(0, width - 270)), height - 3);
        }

        // Yellow playhead with a small arrow at its foot
        const head = Math.min(slot.totalMs, Math.max(0, playheadArg === undefined ? getPlayheadMs() : playheadArg));
        const xh = Math.min(width - 1.5, Math.max(1.5, head * pxPerMs));
        ctx.strokeStyle = WAVE_COLORS.startMarker;
        ctx.lineWidth = 3;
        ctx.beginPath();
        ctx.moveTo(xh, 0);
        ctx.lineTo(xh, height);
        ctx.stroke();
        ctx.fillStyle = WAVE_COLORS.startMarker;
        ctx.beginPath();
        ctx.moveTo(xh - 4, height);
        ctx.lineTo(xh + 4, height);
        ctx.lineTo(xh, height - 6);
        ctx.closePath();
        ctx.fill();
    };

    const redrawWaveform = () => {
        const head = getPlayheadMs();
        drawWaveform(head);
        updateBarDom(head);
    };

    /** The sentence's timeline before the reference waveform is known: its own time slot, no shape. */
    const buildBaseSlot = (i) => {
        const range = getSentenceTimeRangeMs(i);
        if (range) return buildSentenceSlot(i, range.start, range.end - range.start, null);
        const words = ((arrayMainRef.current || [])[i] || '').split(/\s+/).filter(Boolean).length || 1;
        return buildSentenceSlot(i, null, Math.max(MIN_SENTENCE_MS, (words / WORDS_PER_SEC_BASE) * 1000), null);
    };

    /** Builds the timeline for sentence i, then fills in the reference waveform once the lesson audio is analysed. */
    const prepareSlot = async (i) => {
        const lines = arrayMainRef.current;
        if (!lines || lines.length === 0 || i >= lines.length) return;
        const base = buildBaseSlot(i);
        slotRef.current = base;
        redrawWaveform();
        const range = getSentenceTimeRangeMs(i);
        const src = getAudioSrc();
        if (!range || !src) return;
        const trackEnv = await getTrackEnvelope(src);
        if (!trackEnv || slotRef.current !== base) return; // failed, or the user already moved to another sentence
        const from = Math.floor(range.start / WAVE_STEP_MS);
        const to = Math.min(trackEnv.length, Math.ceil(range.end / WAVE_STEP_MS));
        slotRef.current = buildSentenceSlot(i, range.start, range.end - range.start, trackEnv.subarray(from, to));
        redrawWaveform();
    };

    /** One animation loop drives everything that moves: the run (mic sampling, start/stop of recording) and the
     * playhead while the reference audio plays. It ends itself when neither is happening. */
    const startWaveLoop = () => {
        const keepAliveUntil = performance.now() + 800; // audio.play() takes a moment before `paused` flips
        if (waveAnimFrameRef.current) { runRef.current.keepAliveUntil = keepAliveUntil; return; }
        runRef.current.keepAliveUntil = keepAliveUntil;
        const tick = () => {
            waveAnimFrameRef.current = null;
            const run = runRef.current;
            const audioEl = audioElemRef.current;
            const playing = !!(audioEl && !audioEl.paused && !audioEl.ended);
            if (run.active) {
                const t = performance.now() - run.startTs;
                const slot = run.slot;
                // Sample the mic on the 30ms grid of the sentence axis (attack fast / release slow)
                if (!run.stopping && analyserRef.current) {
                    const target = Math.floor(t / WAVE_STEP_MS) + 1;
                    const arr = liveWavePeaksRef.current;
                    if (arr.length < target) {
                        analyserRef.current.getByteTimeDomainData(run.buf);
                        let peak = 0;
                        for (let idx = 0; idx < run.buf.length; idx++) {
                            const dev = Math.abs(run.buf[idx] - 128) / 128;
                            if (dev > peak) peak = dev;
                        }
                        const prev = smoothRef.current;
                        smoothRef.current = prev + (peak > prev ? 0.6 : 0.15) * (peak - prev);
                        while (arr.length < target) arr.push(smoothRef.current);
                    }
                }
                // The playhead reaches the speech: start recognising a moment earlier, record exactly from the speech
                if (!run.stopping && !run.srStarted && t >= slot.regionStartMs - SR_PREROLL_MS) {
                    run.srStarted = true;
                    if (clickMicRef.current) clickMicRef.current(run.index, { continuous: true });
                }
                if (!run.stopping && !run.recStarted && t >= slot.regionStartMs) {
                    run.recStarted = true;
                    try {
                        const rec = perSentenceMediaRecorderRef.current;
                        if (rec && rec.state === 'inactive') rec.start();
                    } catch (e) { console.warn('recorder start failed', e); }
                }
                // The playhead leaves the speech: the sentence is over
                if (!run.stopping && t >= slot.regionEndMs + RUN_GRACE_MS) {
                    run.stopping = true;
                    if (finishRecordingRef.current) finishRecordingRef.current(run.index);
                }
                if (elapsedLabelRef.current) {
                    elapsedLabelRef.current.textContent = t < slot.regionStartMs
                        ? `Bắt đầu sau ${((slot.regionStartMs - t) / 1000).toFixed(1)}s`
                        : `${((Math.min(t, slot.regionEndMs) - slot.regionStartMs) / 1000).toFixed(1)}s`;
                }
            }
            redrawWaveform();
            if (run.active || playing || performance.now() < (run.keepAliveUntil || 0)) {
                waveAnimFrameRef.current = requestAnimationFrame(tick);
            } else {
                playheadMsRef.current = 0; // playback finished: rest at the start
                redrawWaveform();
            }
        };
        waveAnimFrameRef.current = requestAnimationFrame(tick);
    };

    /** Opens the mic for the audio recorder and builds (but does not start) the MediaRecorder. Returns the analyser buffer, or null. */
    const prepareRecordingCapture = async (i) => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            micStreamForRecordingRef.current = stream;

            const AudioContextClass = window.AudioContext || window.webkitAudioContext;
            const audioCtx = new AudioContextClass();
            recorderAudioCtxRef.current = audioCtx;
            try { if (audioCtx.state === 'suspended') await audioCtx.resume(); } catch (e) { }
            const source = audioCtx.createMediaStreamSource(stream);
            const analyser = audioCtx.createAnalyser();
            analyser.fftSize = 2048; // ~43ms window at 44.1kHz, comfortably covers the 30ms sampling interval
            source.connect(analyser);
            analyserRef.current = analyser;

            const mimeTypes = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4'];
            let selectedMime = '';
            for (const m of mimeTypes) { if (MediaRecorder.isTypeSupported(m)) { selectedMime = m; break; } }
            const recorder = new MediaRecorder(stream, selectedMime ? { mimeType: selectedMime } : {});
            const chunks = [];
            recorder.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunks.push(e.data); };
            recorder.onstop = () => {
                const blob = new Blob(chunks, { type: selectedMime || 'audio/webm' });
                sentenceRecordingsRef.current[i] = blob;
                sentenceWavePeaksRef.current[i] = liveWavePeaksRef.current.slice();
            };
            perSentenceMediaRecorderRef.current = recorder;
            return new Uint8Array(analyser.fftSize);
        } catch (e) {
            console.warn('prepareRecordingCapture failed (recording unavailable this attempt)', e);
            return null;
        }
    };

    /** Press mic: the playhead starts sweeping the sentence; recording begins when it reaches the speech. */
    const startRun = async (i) => {
        if (micPermission === 'unsupported') {
            message.error('Trình duyệt này không hỗ trợ nhận dạng giọng nói. Vui lòng dùng Chrome hoặc Edge.');
            return;
        }
        if (micPermission === 'denied') {
            message.error('Microphone bị từ chối. Vào Cài đặt trình duyệt → Microphone → Cho phép rồi tải lại trang.');
            return;
        }
        // Listening and recording are separate actions: stop any reference playback first
        sentenceAudioTokenRef.current++;
        try { audioElemRef.current?.pause(); } catch (e) { }
        setAudioIsPlaying(false);
        stopTakePlayback();
        setLastResult(null);
        setIndexLyric(i);
        const slot =(slotRef.current && slotRef.current.index === i) ? slotRef.current : buildBaseSlot(i);
        const buf = await prepareRecordingCapture(i);
        if (!buf) {
            message.warning('Không thể mở microphone. Kiểm tra quyền trình duyệt và thử lại.');
            return;
        }
        liveWavePeaksRef.current = [];
        smoothRef.current = 0;
        runRef.current = {
            active: true, index: i, slot, buf,
            startTs: performance.now(),
            srStarted: false, recStarted: false, stopping: false,
        };
        setRunIndex(i);
        startWaveLoop();
    };
    startRunRef.current = startRun;

    /** Tears down the recorder/stream/analyser/animation loop. Returns a Promise that resolves only after the
     * recorder's onstop has actually stored the Blob into sentenceRecordingsRef — callers that need the Blob right
     * away (e.g. to check "has every sentence been recorded yet") must await this. `discard` drops the audio. */
    const stopRecordingCapture = (discard = false) => {
        return new Promise((resolve) => {
            try {
                runRef.current.active = false;
                setRunIndex(-1);
                if (waveAnimFrameRef.current) { cancelAnimationFrame(waveAnimFrameRef.current); waveAnimFrameRef.current = null; }
                playheadMsRef.current = 0;
                const recorder = perSentenceMediaRecorderRef.current;
                if (recorder && recorder.state !== 'inactive') {
                    const originalOnStop = recorder.onstop;
                    recorder.onstop = (ev) => {
                        try { if (!discard && originalOnStop) originalOnStop(ev); } finally { redrawWaveform(); resolve(); }
                    };
                    recorder.stop();
                } else {
                    redrawWaveform();
                    resolve();
                }
                perSentenceMediaRecorderRef.current = null;
                if (micStreamForRecordingRef.current) {
                    micStreamForRecordingRef.current.getTracks().forEach(t => t.stop());
                    micStreamForRecordingRef.current = null;
                }
                if (recorderAudioCtxRef.current) {
                    recorderAudioCtxRef.current.close().catch(() => { });
                    recorderAudioCtxRef.current = null;
                }
                analyserRef.current = null;
            } catch (e) { console.warn('stopRecordingCapture error', e); resolve(); }
        });
    };

    /** Abandon the run (pressed again during the run-up, or moved to another sentence): nothing is saved. */
    const cancelRun = async () => {
        const run = runRef.current;
        if (!run.active) return;
        run.stopping = true;
        userStoppedRef.current = true;
        setRecordingIndex(-1);
        const captureStopped = stopRecordingCapture(true); // ends the run (loop, recorder, mic) right away
        await haltSpeechRecognition(true);
        await captureStopped;
    };

    /** Encodes raw mono Float32 PCM samples as a WAV Blob (16-bit PCM) — universally playable
     * and simple to build without pulling in an extra library. */
    const encodeWav = (samples, sampleRate) => {
        const buffer = new ArrayBuffer(44 + samples.length * 2);
        const view = new DataView(buffer);
        const writeString = (offset, str) => { for (let i = 0; i < str.length; i++) view.setUint8(offset + i, str.charCodeAt(i)); };
        writeString(0, 'RIFF');
        view.setUint32(4, 36 + samples.length * 2, true);
        writeString(8, 'WAVE');
        writeString(12, 'fmt ');
        view.setUint32(16, 16, true);
        view.setUint16(20, 1, true); // PCM
        view.setUint16(22, 1, true); // mono
        view.setUint32(24, sampleRate, true);
        view.setUint32(28, sampleRate * 2, true); // byte rate (sampleRate * blockAlign)
        view.setUint16(32, 2, true); // block align
        view.setUint16(34, 16, true); // bits per sample
        writeString(36, 'data');
        view.setUint32(40, samples.length * 2, true);
        let offset = 44;
        for (let i = 0; i < samples.length; i++, offset += 2) {
            const s = Math.max(-1, Math.min(1, samples[i]));
            view.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7fff, true);
        }
        return new Blob([view], { type: 'audio/wav' });
    };

    /** Once every sentence has been recorded, decode all per-sentence audio Blobs, concatenate
     * them into one continuous track, encode as WAV, upload to R2, and persist the URL. */
    const mergeAndUploadFullRecording = async () => {
        if (isMergingRecording) return; // already in progress
        try {
            setIsMergingRecording(true);
            const total = arrayMainRef.current.length;
            const blobs = [];
            for (let i = 0; i < total; i++) {
                const b = sentenceRecordingsRef.current[i];
                if (!b) { setIsMergingRecording(false); return; } // not actually complete — bail out quietly
                blobs.push(b);
            }

            const AudioContextClass = window.AudioContext || window.webkitAudioContext;
            const audioCtx = new AudioContextClass();
            const buffers = [];
            for (const blob of blobs) {
                const arrayBuf = await blob.arrayBuffer();
                buffers.push(await audioCtx.decodeAudioData(arrayBuf));
            }
            const sampleRate = buffers[0].sampleRate;
            const totalLength = buffers.reduce((sum, b) => sum + b.length, 0);
            const merged = new Float32Array(totalLength);
            let offset = 0;
            for (const buf of buffers) {
                merged.set(buf.getChannelData(0), offset);
                offset += buf.length;
            }
            try { audioCtx.close(); } catch (e) { }

            const wavBlob = encodeWav(merged, sampleRate);
            const fd = new FormData();
            fd.append('file', wavBlob, `speaking_full_${dataLessonRef.current?.id || 'lesson'}_${Date.now()}.wav`);
            const resp = await axios.post(`${process.env.REACT_APP_API_URL}/uploadAudio`, fd, {
                headers: { 'Content-Type': 'multipart/form-data', Authorization: `Bearer ${cookies?.user?.token}` },
                timeout: 120000,
            });
            const url = resp?.data;
            if (url) {
                setFullRecordingUrl(url);
                // Persist as a special summary record (position -1) so "Nghe lại" survives page reloads
                try {
                    await postRecord(dataLessonRef.current.id, -1, `${toLowerCamelCase(storyRef.current)}Full`, 'full_recording', 0, url);
                } catch (e) { console.warn('Persist full recording url failed', e); }
                message.success('Đã ghép và lưu bản ghi cả bài lên R2!');
            }
        } catch (e) {
            console.error('mergeAndUploadFullRecording error', e);
            message.error('Không thể ghép/lưu bản ghi cả bài');
        } finally {
            setIsMergingRecording(false);
        }
    };

    /** Starts speech recognition for sentence i (the audio itself is recorded by the timeline run, see startRun) */
    const clickMic = async (i, opts) => {
        if (micPermission === 'unsupported') {
            message.error('Trình duyệt này không hỗ trợ nhận dạng giọng nói. Vui lòng dùng Chrome hoặc Edge.');
            return;
        }
        if (micPermission === 'denied') {
            message.error('Microphone bị từ chối. Vào Cài đặt trình duyệt → Microphone → Cho phép rồi tải lại trang.');
            return;
        }
        userStoppedRef.current = false;
        setIndexLyric(i);
        setRecordingIndex(i);
        setShowScoreModal(false);
        setScoreResult(null);
        try { SpeechRecognition.getRecognition().abort(); } catch (e) { }
        resetTranscript();
        sentenceTranscriptRef.current = '';
        sentenceLastTranscriptRef.current = '';
        try {
            await SpeechRecognition.startListening({ language: 'en-US', continuous: !!(opts && opts.continuous) });
            hasStartedRef.current = true;
        } catch (err) {
            console.error('startListening error:', err);
            if (err.name === 'not-allowed' || err.message?.includes('not-allowed')) {
                setMicPermission('denied');
                message.error('Không thể truy cập microphone. Kiểm tra quyền trình duyệt và thử lại.');
            } else {
                message.warning('Không thể bắt đầu ghi âm. Thử lại sau giây lát.');
            }
            setRecordingIndex(-1);
        }
    };
    // Keep a stable ref so the listening effect can call clickMic without stale closure
    clickMicRef.current = clickMic;

    // react-speech-recognition's stopListening()/abortListening() only resolve once the engine actually fires
    // `end` — if it never started or already ended (network error, no-speech timeout) they'd wait forever.
    const haltSpeechRecognition = async (abort) => {
        try {
            await Promise.race([
                abort ? SpeechRecognition.abortListening() : SpeechRecognition.stopListening(),
                new Promise((resolve) => setTimeout(resolve, 1000)),
            ]);
        } catch (e) { }
    };

    const stopMic = async () => {
        userStoppedRef.current = true;
        setRecordingIndex(-1);
        await haltSpeechRecognition(false);
        // If in continuous recording mode, append the last sentence transcript to accumulator
        try {
            if (recordingAllRef.current) {
                const t = transcriptRef.current?.trim();
                if (t) {
                    fullTranscriptRef.current = (fullTranscriptRef.current ? fullTranscriptRef.current + ' ' : '') + t;
                }
                // clear per-sentence transcript after appending
                resetTranscript();
            }
        } catch (e) { console.error('stopMic append error', e); }

        // Stop the audio capture + waveform loop, and wait for the Blob to actually be stored before returning.
        await stopRecordingCapture();
    };

    /** Uploads this session's recording of sentence i to R2 (a new file every time) and returns its url. */
    const uploadTake = async (i) => {
        const blob = sentenceRecordingsRef.current[i];
        if (!blob || !blob.size) return null;
        const ext = /mp4|aac/.test(blob.type || '') ? 'm4a' : 'webm';
        const fd = new FormData();
        fd.append('file', blob, `speaking_${dataLessonRef.current?.id || 'lesson'}_${toLowerCamelCase(storyRef.current)}_${i}_${Date.now()}.${ext}`);
        const resp = await axios.post(`${process.env.REACT_APP_API_URL}/uploadAudio`, fd, {
            headers: { 'Content-Type': 'multipart/form-data', Authorization: `Bearer ${cookies?.user?.token}` },
            timeout: 60000,
        });
        return typeof resp?.data === 'string' ? resp.data : null;
    };

    /** Saved recording urls of sentence i, newest first. */
    const takeUrlsFor = (i) => parseTakeUrls(recordMain.find(r => +r.position === i)?.audio);

    /** Play / pause one saved recording (or this session's local copy) in the player on the left. */
    const toggleTake = (key, src) => {
        const el = takeAudioRef.current;
        if (!el) return;
        if (playingTakeKey === key) { el.pause(); setPlayingTakeKey(null); return; }
        try { audioElemRef.current?.pause(); } catch (e) { }
        sentenceAudioTokenRef.current++;
        setAudioIsPlaying(false);
        el.src = src;
        setPlayingTakeKey(key);
        el.play().catch(() => { setPlayingTakeKey(null); message.error('Không phát được bản ghi này'); });
    };
    const stopTakePlayback = () => {
        try { takeAudioRef.current?.pause(); } catch (e) { }
        setPlayingTakeKey(null);
    };

    /** The recording of sentence i is over (the playhead left the speech, or the user pressed stop): score it right away,
     * then upload this take and save it with the score. The learner stays on the sentence (Next moves on). */
    const finishRecording = async (i) => {
        try {
            runRef.current.stopping = true;
            await stopMic();
            setRecordingIndex(-1);
            const spoken = (sentenceTranscriptRef.current || transcriptRef.current)?.trim();
            // clear per-sentence buffers after use
            sentenceTranscriptRef.current = '';
            sentenceLastTranscriptRef.current = '';
            if (!spoken) {
                setLastResult(null);
                message.warning('Không nhận được giọng nói. Hãy bấm ghi lại và nói to, rõ hơn.');
                return;
            }
            const sentenceWords = (arrayMain[i] || '').split(' ');
            setLastResult({ position: i, point: scoreText(sentenceWords, spoken), text: spoken });
            setSavingTake(true);
            let urls = takeUrlsFor(i);
            try {
                const url = await uploadTake(i);
                if (url) urls = [url, ...urls];
            } catch (e) {
                console.warn('Upload take failed', e);
                message.warning('Chưa lưu được bản ghi lên máy chủ, nhưng điểm vẫn được lưu.');
            }
            await handleRecord(spoken, i, urls);
            // If every sentence in the lesson now has a recorded Blob, merge them into one file and save to R2
            const allRecorded = arrayMain.length > 0 && arrayMain.every((_, idx) => !!sentenceRecordingsRef.current[idx]);
            if (allRecorded) mergeAndUploadFullRecording();
        } catch (e) {
            console.error('finishRecording error', e);
        } finally {
            setSavingTake(false);
        }
    };
    finishRecordingRef.current = finishRecording;

    // Mic button: start the timeline run for this sentence; press again to stop early (or cancel during the run-up)
    const handlePerSentenceRecord = async (i) => {
        try {
            const run = runRef.current;
            if (run.active && run.index === i) {
                if (run.recStarted) await finishRecording(i);
                else await cancelRun();
                return;
            }
            await startRun(i);
        } catch (e) {
            console.error('handlePerSentenceRecord error', e);
        }
    };

    /**
     * Schedule auto-advance: highlight each lyric line for (wordCount / wordsPerSecond) seconds
     * then move to the next. Respects readingSpeedRef for speed changes.
     * wordsPerSecond baseline = 1.4 words/sec at 1x (relaxed teaching audio pace)
     */
    const WORDS_PER_SEC_BASE = 1.4;
    const MIN_SENTENCE_MS = 2500; // never less than 2.5s per sentence
    const scheduleAutoAdvance = (startIndex) => {
        clearAutoAdvance(); // clear any existing timer
        const lines = arrayMainRef.current;
        if (!lines || lines.length === 0) return;

        const advanceTo = (idx) => {
            if (idx >= lines.length) return; // reached end
            setIndexLyric(idx);

            const wordCount = (lines[idx] || '').split(/\s+/).filter(Boolean).length || 1;
            const speed = readingSpeedRef.current;
            const durationMs = Math.max(MIN_SENTENCE_MS, (wordCount / (WORDS_PER_SEC_BASE * speed)) * 1000);

            autoAdvanceTimerRef.current = setTimeout(() => {
                if (!recordingAllRef.current) return; // stopped externally
                advanceTo(idx + 1);
            }, durationMs);
        };

        advanceTo(startIndex);
    };

    /** Schedule auto-advance that triggers per-sentence recording (clickMic) for each sentence.
     *  This will call `clickMic(i)` when a sentence becomes active, wait the duration,
     *  then call `stopMic()` and advance to the next sentence. It respects `recordingAllRef`.
     */
    const scheduleAutoAdvancePerSentence = (startIndex) => {
        clearAutoAdvance();
        const lines = arrayMainRef.current;
        if (!lines || lines.length === 0) return;

        const advanceTo = async (idx) => {
            if (idx >= lines.length) return;
            setIndexLyric(idx);

            // Prefer timestamps for camera sync when available
            const tsKey = toLowerCamelCase(storyRef.current);
            const tsArr = timestampsRef.current[tsKey] || [];
            let durationMs;
            if (tsArr && tsArr.length > idx) {
                const start = tsArr[idx] || 0;
                const end = (idx + 1 < tsArr.length) ? tsArr[idx + 1] : (start + MIN_SENTENCE_MS);
                const diff = Math.max(0, end - start);
                durationMs = Math.max(MIN_SENTENCE_MS, diff);
            } else {
                const wordCount = (lines[idx] || '').split(/\s+/).filter(Boolean).length || 1;
                const speed = readingSpeedRef.current;
                durationMs = Math.max(MIN_SENTENCE_MS, (wordCount / (WORDS_PER_SEC_BASE * speed)) * 1000);
            }

            // Start per-sentence recording (non-continuous)
            try {
                if (clickMicRef.current) await clickMicRef.current(idx);
            } catch (e) {
                console.error('Per-sentence clickMic error', e);
            }

            autoAdvanceTimerRef.current = setTimeout(async () => {
                // stop per-sentence recording and proceed
                try { await stopMic(); } catch (e) { }
                // Hold here so the learner can actually see the finished waveform (their recorded
                // voice) before the view jumps to the next sentence — also gives speech recognition
                // time to finalize the interim transcript.
                await new Promise(res => setTimeout(res, 1000));
                if (!recordingAllRef.current) return;
                advanceTo(idx + 1);
            }, durationMs);
        };

        advanceTo(startIndex);
    };

    const clearAutoAdvance = () => {
        if (autoAdvanceTimerRef.current) {
            clearTimeout(autoAdvanceTimerRef.current);
            autoAdvanceTimerRef.current = null;
        }
    };

    /** Score a single sentence against a result text */
    const scoreText = (sentenceWords, resultText) => {
        const resultWords = resultText
            .replace(/[!@#$%^&*(),.?"":{}|<>""]/g, '')
            .toLowerCase()
            .split(/\s+/);
        let matches = 0;
        sentenceWords.forEach(w => {
            const clean = w.replace(/[!@#$%^&*(),.?"":{}|<>""]/g, '').toLowerCase();
            if (resultWords.includes(clean)) matches++;
        });
        return Math.ceil((matches / (sentenceWords.length || 1)) * 100);
    };

    /** POST a single record */
    const postRecord = (lesson_id, position, track, text, point, audio) =>
        axios.post(`${process.env.REACT_APP_API_URL}/record`, {
            lesson_id, position, track, text, point, audio,
        }, {
            headers: {
                Accept: 'application/json', 'Content-Type': 'application/json',
                Authorization: `Bearer ${cookies?.user?.token}`
            },
        });

    const handleRecord = async (result, positionOverride, audioUrls) => {
        try {
            const position = positionOverride !== undefined ? positionOverride : indexLyricRef.current;
            const item = arrayMainRef.current[position]?.split(' ') || [];
            const point = scoreText(item, result);
            // always re-send the sentence's saved recordings, otherwise saving the score would wipe them
            const audio = audioUrls && audioUrls.length ? serializeTakeUrls(audioUrls) : undefined;
            await postRecord(dataLessonRef.current.id, position, toLowerCamelCase(storyRef.current), result, point, audio);
            await getRecords();
        } catch (error) { console.error(error); }
    };

    /** Skip sentence (mark as skipped with 0 point) */
    const skipSentence = async (i) => {
        try {
            const keptTakes = takeUrlsFor(i);
            await postRecord(dataLessonRef.current.id, i, toLowerCamelCase(storyRef.current), 'skipped', 0, keptTakes.length ? serializeTakeUrls(keptTakes) : undefined);
            await getRecords();
            message.info('Đã bỏ qua câu này');
            // Auto advance to next sentence
            if (i < arrayMainRef.current.length - 1) {
                setIndexLyric(i + 1);
            }
        } catch (error) {
            console.error(error);
            message.error('Không thể bỏ qua câu này');
        }
    };

    const lesson = async () => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/lesson/getBySlug/${slug}?_t=${Date.now()}`);
            setdataLesson(response.data[0]);
            try { window.dispatchEvent(new Event('page-ready')); } catch (e) { }
        } catch (error) { console.error(error); }
    };

    const getRecords = async () => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/user/record`, {
                headers: {
                    Accept: 'application/json', 'Content-Type': 'application/json',
                    Authorization: `Bearer ${cookies?.user?.token}`
                },
            });
            setRecords(response.data);
            return response.data;
        } catch (error) { console.error(error); }
    };

    const userTopic = async () => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/usertopic`, {
                headers: {
                    Accept: 'application/json', 'Content-Type': 'application/json',
                    Authorization: `Bearer ${cookies?.user?.token}`
                },
            });
            setDataUserTopic(response?.data[0]);
        } catch (error) { console.error(error); }
    };

    // ── Audio helpers (for camera-recording mode) ──────────────────────────
    const isAudioUrl = (url) => {
        if (!url || typeof url !== 'string') return false;
        return /\.(mp3|m4a|wav|ogg|webm|aac)(\?.*)?$/i.test(url) || /^data:audio\//.test(url);
    };
    const proxyIfR2 = (u) => {
        if (!u || typeof u !== 'string') return u;
        if (u.includes('.r2.cloudflarestorage.com')) {
            const base = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
            return `${base}/media/proxy?url=${encodeURIComponent(u)}`;
        }
        return u;
    };
    const resolveAudio = (val) => {
        if (!val) return null;
        if (typeof val === 'string') return isAudioUrl(val) ? val : null;
        if (typeof val === 'object') {
            if (Array.isArray(val) && val.length) {
                const first = val[0];
                if (typeof first === 'string') return isAudioUrl(first) ? first : null;
                if (first?.url && isAudioUrl(first.url)) return first.url;
            }
            if (val.url && isAudioUrl(val.url)) return val.url;
        }
        return null;
    };
    const getAudioSrc = () => {
        const storyKey = storyRef.current;
        const dl = dataLessonRef.current;
        const raw = storyKey === 'Main Story' ? resolveAudio(dl?.mainStoryAudio)
            : storyKey === 'Vocabulary' ? resolveAudio(dl?.vocabularyAudio)
                : storyKey === 'Mini Story' ? resolveAudio(dl?.miniStoryAudio)
                    : storyKey === 'POV' ? resolveAudio(dl?.POVAudio)
                        : storyKey === 'Comment' ? resolveAudio(dl?.commentAudio)
                            : null;
        return proxyIfR2(raw);
    };

    // Point the audio element at the reference track as soon as the lesson/story is known,
    // so the first Play (and seeking along the sentence timeline) doesn't wait for the file to start loading.
    useEffect(() => {
        const src = getAudioSrc();
        const audioEl = audioElemRef.current;
        if (!src || !audioEl) return;
        if (audioEl.src !== src) {
            audioEl.src = src;
            audioEl.load();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [story, dataLesson]);
    const downloadAudio = () => {
        const src = getAudioSrc();
        if (!src) { message.error('Không tìm thấy file âm thanh!'); return; }
        const link = document.createElement('a');
        link.href = src;
        link.download = `${dataLessonRef.current?.title || 'audio'}_${(storyRef.current || '').replace(/\s/g, '_')}.mp3`;
        link.target = '_blank';
        link.click();
    };

    // ── Play the lesson's reference audio for a single sentence ────────────
    const getSentenceTimeRangeMs = (i) => {
        const tsKey = toLowerCamelCase(storyRef.current);
        const tsArr = timestampsRef.current[tsKey] || [];
        if (tsArr && tsArr.length > i && typeof tsArr[i] === 'number') {
            const start = tsArr[i] || 0;
            const end = (i + 1 < tsArr.length && typeof tsArr[i + 1] === 'number') ? tsArr[i + 1] : (start + 6000);
            return { start, end: Math.max(end, start + 500) };
        }
        return null;
    };

    /** Plays just this sentence's segment of the lesson audio; resolves once it's done (or immediately if no audio). */
    const playSentenceAudio = (i, offsetMs = 0) => {
        return new Promise((resolve) => {
            const audioEl = audioElemRef.current;
            const src = getAudioSrc();
            if (!audioEl || !src) { resolve(); return; }

            const token = ++sentenceAudioTokenRef.current;
            const range = getSentenceTimeRangeMs(i);

            let safetyTimer = null;
            const cleanup = () => {
                audioEl.removeEventListener('timeupdate', onTimeUpdate);
                audioEl.removeEventListener('ended', onDone);
                audioEl.removeEventListener('error', onDone);
                if (safetyTimer) clearTimeout(safetyTimer);
            };
            const onDone = () => {
                if (sentenceAudioTokenRef.current !== token) return;
                cleanup();
                try { audioEl.pause(); } catch (e) { }
                setAudioIsPlaying(false);
                resolve();
            };
            const onTimeUpdate = () => {
                if (range && audioEl.currentTime * 1000 >= range.end) onDone();
            };
            const seekAndPlay = () => {
                if (sentenceAudioTokenRef.current !== token) { resolve(); return; }
                try { audioEl.muted = false; audioEl.currentTime = range ? (range.start + Math.min(offsetMs, Math.max(0, range.end - range.start - 200))) / 1000 : 0; } catch (e) { }
                audioEl.addEventListener('timeupdate', onTimeUpdate);
                audioEl.addEventListener('ended', onDone);
                audioEl.addEventListener('error', onDone);
                setAudioIsPlaying(true);
                audioEl.play().catch(onDone);
                // Safety net: if this track has no usable timestamps, never block on the full track playing out
                safetyTimer = setTimeout(onDone, range ? (range.end - range.start) + 1500 : 8000);
            };

            if (audioEl.src !== src) {
                audioEl.src = src;
                audioEl.addEventListener('loadedmetadata', seekAndPlay, { once: true });
                audioEl.load();
            } else if (audioEl.readyState >= 1) {
                seekAndPlay();
            } else {
                audioEl.addEventListener('loadedmetadata', seekAndPlay, { once: true });
            }
        });
    };

    /** Select a sentence: move the cursor to it. Nothing plays by itself — the Play button plays the reference. */
    const selectSentence = (i) => {
        if (runRef.current.active) cancelRun();
        stopTakePlayback();
        try { audioElemRef.current?.pause(); } catch (e) { }
        setAudioIsPlaying(false);
        setIndexLyric(i);
    };
    const addTimestamps = async () => {
        try {
            const dl = dataLessonRef.current;
            const temp = {};
            const dataKeys = ['mainStory', 'miniStory', 'POV', 'comment', 'vocabulary'];
            for (const key of dataKeys) {
                if (dl?.[key]) {
                    const mapped = key === 'POV' ? 'pOV' : key;
                    try {
                        // Filter out blank lines exactly like convertString() does for arrayMain,
                        // so timestamps stay index-aligned with the sentences shown on screen.
                        temp[mapped] = dl[key].split('\n')
                            .filter(el => {
                                const closeBracketIndex = el.indexOf(']');
                                const text = closeBracketIndex >= 0 ? el.slice(closeBracketIndex + 1) : el;
                                return text.trim();
                            })
                            .map(el => {
                                const start = el.indexOf(']');
                                if (start >= 0) {
                                    return convertTimeToMilliseconds(el.slice(1, el.indexOf(']')));
                                }
                                return 0;
                            });
                    } catch (e) {
                        temp[mapped] = [];
                    }
                }
            }
            setTimestamps(temp);
        } catch (e) {
            console.error('addTimestamps error', e);
            setTimestamps({});
        }
    };

    // Helper: create a MediaRecorder for a given stream with consistent handlers
    // Batch scoring helper: post aligned chunks and update local records
    const batchScoreTranscript = async (currentTranscript) => {
        try {
            const lesson = dataLessonRef.current;
            if (!lesson || !currentTranscript) return;
            setIsScoringAll(true);

            const linesWithPos = arrayMainRef.current.map((l, idx) => ({ line: l, pos: idx }));
            const validLines = linesWithPos.filter(l => l.line?.trim());
            const aligned = alignTranscript(currentTranscript, validLines.map(v => v.line));

            const posts = validLines.map((v, idx) => {
                const chunk = aligned[idx]?.chunk?.join(' ') || currentTranscript;
                const point = aligned[idx]?.point || 0;
                const originalPos = v.pos;
                return postRecord(lesson.id, originalPos, toLowerCamelCase(storyRef.current), chunk, point);
            });

            await Promise.allSettled(posts);

            // try to refresh authoritative records, but fallback to local
            let base;
            try { const fetched = await getRecords(); base = Array.isArray(fetched) ? fetched : (Array.isArray(records) ? [...records] : []); }
            catch (fetchErr) { base = Array.isArray(records) ? [...records] : []; }

            for (const [idx, v] of validLines.entries()) {
                const originalPos = v.pos;
                const chunk = aligned[idx]?.chunk?.join(' ') || '';
                const point = aligned[idx]?.point || 0;
                const spokenLine = aligned[idx]?.spokenLine;
                const existing = base.find(r => +r.position === originalPos && +r.lesson_id === +lesson.id);
                if (existing) {
                    existing.text = chunk || existing.text;
                    existing.point = point || existing.point;
                    existing.spokenLine = spokenLine || existing.spokenLine;
                } else {
                    base.push({ lesson_id: lesson.id, position: originalPos, track: toLowerCamelCase(storyRef.current), text: chunk, point, spokenLine });
                }
            }

            setRecords(base);
            setRecordMain(base.filter(rec => +rec.lesson_id === +lesson.id && toCamelCase(rec.track) === storyRef.current));

            const scoredLines = validLines.map((v, idx) => ({
                lyric: v.line,
                pos: v.pos,
                point: aligned[idx]?.point || 0,
                spokenLine: aligned[idx]?.spokenLine,
            }));
            const avgPoint = Math.round(scoredLines.reduce((s, l) => s + l.point, 0) / (scoredLines.length || 1));
            setScoreResult({ avgPoint, lines: scoredLines, totalLines: validLines.length });

            if (lessonHasSpecialUi()) {
                setAttemptCount(c => c + 1);
            } else {
                setShowScoreModal(true);
            }
        } catch (e) {
            console.error('batchScoreTranscript error', e);
            message.error('Có lỗi khi chấm điểm. Vui lòng thử lại.');
        } finally {
            setIsScoringAll(false);
            setRecordingAll(false);
            fullTranscriptRef.current = '';
            resetTranscript();
        }
    };

    // Rebuild timestamps when lesson data loads / story changes
    useEffect(() => { if (dataLesson?.id) addTimestamps(); }, [dataLesson]); // eslint-disable-line react-hooks/exhaustive-deps

    // Cleanup auto-advance timer on unmount
    useEffect(() => {
        return () => { clearAutoAdvance(); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Stop auto-advance when recordingAll is turned off
    useEffect(() => {
        if (!recordingAll) clearAutoAdvance();
    }, [recordingAll]);

    useEffect(() => {
        if (recordingAll && listening) {
            scheduleAutoAdvancePerSentence(indexLyricRef.current);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [readingSpeed]);

    const fetchUserInfo = async () => {
        if (!cookies?.user?.token) return;
        try {
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/user/me`, {
                headers: {
                    'Authorization': `Bearer ${cookies?.user?.token}`
                }
            });
            setUserInfo(response.data);
        } catch (error) {
            console.error('Error fetching user info:', error);
        }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { lesson(); if (cookies?.user) { getRecords(); userTopic(); fetchUserInfo(); } }, []);

    // While in whole-lesson recording mode, accumulate incremental transcript deltas
    useEffect(() => {
        if (!recordingAllRef.current) return;
        try {
            const current = transcript || '';
            const last = lastTranscriptRef.current || '';
            if (current.length > last.length) {
                const delta = current.slice(last.length).trim();
                if (delta) {
                    fullTranscriptRef.current = (fullTranscriptRef.current ? fullTranscriptRef.current + ' ' : '') + delta;
                }
                lastTranscriptRef.current = current;
            }
        } catch (e) { console.error('Accumulate transcript delta error', e); }
    }, [transcript]);

    // While in per-sentence recording mode, accumulate transcript deltas for that sentence
    useEffect(() => {
        // Only active for per-sentence (not whole-lesson) recording
        if (recordingAllRef.current) return;
        if (recordingIndex === -1) return;
        try {
            const current = transcript || '';
            const last = sentenceLastTranscriptRef.current || '';
            if (current.length > last.length) {
                const delta = current.slice(last.length).trim();
                if (delta) {
                    sentenceTranscriptRef.current = (sentenceTranscriptRef.current ? sentenceTranscriptRef.current + ' ' : '') + delta;
                }
                sentenceLastTranscriptRef.current = current;
            }
        } catch (e) { console.error('Per-sentence accumulate error', e); }
    }, [transcript, recordingIndex]);

    // The speech engine stopping on its own (error / no-speech timeout) must not end a running timeline; saving and
    // scoring happen once, in finishRecording(), when the playhead leaves the speech.
    useEffect(() => {
        if (listening) return;
        if (!hasStartedRef.current) return;
        hasStartedRef.current = false;
        if (runRef.current.active && !runRef.current.stopping) return;
        setRecordingIndex(-1);
    }, [listening]);

    useEffect(() => {
        const trackData = story === "Main Story" ? dataLesson?.mainStory
            : story === "Vocabulary" ? dataLesson?.vocabulary
                : story === 'Mini Story' ? dataLesson?.miniStory
                    : story === 'POV' ? dataLesson?.POV : dataLesson?.comment;
        convertString(trackData);
        const opts = [];
        if (dataLesson?.mainStory?.trim()) opts.push('Main Story');
        if (dataLesson?.vocabulary?.trim()) opts.push('Vocabulary');
        if (dataLesson?.miniStory?.trim()) opts.push('Mini Story');
        if (dataLesson?.POV?.trim()) opts.push('POV');
        if (dataLesson?.comment?.trim()) opts.push('Comment');
        setOptions(opts);
        // For lessons configured with specialUi we want a fresh UI on first load,
        // otherwise display existing records. Initialize tracking map if needed.
        if (!window.__bai01_initialized) window.__bai01_initialized = {};
        if (lessonHasSpecialUi() && !window.__bai01_initialized[slug]) {
            // initial clear for a fresh UI
            setRecordMain([]);
            window.__bai01_initialized[slug] = true;
        } else {
            const filtered = records.filter(rec => +rec.lesson_id === +dataLesson.id && toCamelCase(rec.track) === story);
            setRecordMain(dedupeLatestRecordsByPosition(filtered));
        }
        // Restore the whole-lesson merged recording (if one was saved before) so "Nghe lại" survives reloads
        const fullTrack = `${toLowerCamelCase(story)}Full`;
        const fullRec = records.find(rec => +rec.lesson_id === +dataLesson.id && rec.track === fullTrack && +rec.position === -1);
        setFullRecordingUrl(fullRec?.audio || null);
    }, [story, dataLesson, records]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (dataUserTopic?.lesson_id) {
            const vipExpireAt = userInfo?.vip_expire_at || cookies?.user?.vip_expire_at;
            if ((dayjsInstance(vipExpireAt)?.format("YYYY-MM-DD") > dayjsInstance(Date())?.format("YYYY-MM-DD")
                && vipExpireAt !== null)
                || +dataUserTopic?.lesson_id >= +dataLesson.id || +index === 0) {
            } else { navigate("/"); }
        }
        if (index === undefined || !cookies?.user) navigate("/login");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cookies, userInfo]);

    // Reset attempt target when lesson loads
    useEffect(() => {
        if (dataLesson?.id) {
            // if backend provides a target, use it; otherwise default to 3
            setAttemptTarget(dataLesson?.practice_target || 3);
            // Reset in-memory attempt count whenever lesson (re)loads
            setAttemptCount(0);
        }
    }, [dataLesson]);

    // Default settings for Speaking page
    useEffect(() => {
        setShowIPA(false);
        setShowTranslation(false);
    }, [slug]);

    // Stats (memoized)
    const totalLines = useMemo(() => arrayMain.length, [arrayMain]);
    const completedLines = useMemo(() => recordMain.filter(r => r.text !== 'skipped').length, [recordMain]);
    const skippedLines = useMemo(() => recordMain.filter(r => r.text === 'skipped').length, [recordMain]);
    const completionPercent = useMemo(() => totalLines > 0 ? Math.round((completedLines / totalLines) * 100) : 0, [totalLines, completedLines]);
    const avgScore = useMemo(() => {
        const scored = recordMain.filter(r => r.text !== 'skipped');
        return scored.length > 0 ? Math.round(scored.reduce((sum, r) => sum + (r.point || 0), 0) / scored.length) : 0;
    }, [recordMain]);

    return (
        <div className="min-h-screen bg-gradient-to-b from-[#0c1929] to-[#162d50] pb-56">
            {/* Hidden audio for reference-sentence + full-track playback */}
            <audio
                ref={audioElemRef}
                onTimeUpdate={() => {
                    if (audioElemRef.current) setAudioCurrentTime(audioElemRef.current.currentTime * 1000);
                }}
                onEnded={() => { setAudioIsPlaying(false); }}
                preload="metadata"
            />

            {/* Player for the learner's saved recordings (left column) */}
            <audio
                ref={takeAudioRef}
                onEnded={() => setPlayingTakeKey(null)}
                onError={() => { if (takeAudioRef.current?.src) { setPlayingTakeKey(null); message.error('Không phát được bản ghi này'); } }}
                preload="none"
            />

            {/* Header */}
            <div className="bg-[#0a1628]/80 backdrop-blur-md border-b border-white/10 sticky top-0 z-20">
                <div className="max-w-screen-xl mx-auto px-4 py-3 flex items-center justify-between">
                    <Breadcrumb
                        className="[&_.ant-breadcrumb-link]:!text-blue-300 [&_.ant-breadcrumb-separator]:!text-blue-400 [&_a]:!text-blue-300 [&_a:hover]:!text-white"
                        items={[
                            { title: <Link to="/"><HomeOutlined /> Trang chủ</Link> },
                            { title: <Link to="/topic">Topic</Link> },
                            { title: <Link to={"/lesson/" + dataLesson?.course_slug}>{dataLesson?.course_title}</Link> },
                            { title: <span className="!text-white">{dataLesson?.title}</span> },
                        ]}
                    />
                </div>
            </div>

            {/* Mic permission / browser support warning */}
            {(micPermission === 'denied' || micPermission === 'unsupported') && (
                <div className="max-w-screen-xl mx-auto px-4 pt-4">
                    <div className="flex items-start gap-3 bg-red-500/10 border border-red-500/30 rounded-xl px-5 py-4">
                        <WarningOutlined className="text-red-400 text-xl mt-0.5 flex-shrink-0" />
                        <div>
                            <p className="text-red-400 font-semibold">
                                {micPermission === 'unsupported'
                                    ? 'Trình duyệt không hỗ trợ nhận dạng giọng nói'
                                    : 'Microphone bị chặn — không thể luyện nói'}
                            </p>
                            <p className="text-gray-400 text-sm mt-1">
                                {micPermission === 'unsupported'
                                    ? 'Vui lòng dùng Google Chrome hoặc Microsoft Edge để sử dụng tính năng này.'
                                    : 'Bấm vào biểu tượng 🔒 trên thanh địa chỉ → chọn Microphone → Cho phép → sau đó tải lại trang.'}
                            </p>
                        </div>
                    </div>
                </div>
            )}

            <div className="max-w-screen-xl mx-auto px-4 py-6">
                <h1 className="text-2xl md:text-3xl font-bold text-white text-center mb-6">{dataLesson?.title}</h1>

                {/* Track Selector */}
                <div className="flex justify-center mb-6">
                    <div className="bg-[#1a2d4a] rounded-xl p-1 inline-flex gap-1 flex-wrap justify-center">
                        {options.map(opt => (
                            <button key={opt} onClick={() => { setStory(opt); setIndexLyric(0); }}
                                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${story === opt ? 'bg-blue-500 text-white shadow-lg shadow-blue-500/30'
                                    : 'text-blue-200 hover:text-white hover:bg-white/10'
                                    }`}>
                                {opt}
                            </button>
                        ))}
                    </div>
                </div>

                {(() => {
                    const activeRec = recordMain.find(r => +r.position === indexLyric);
                    const isRunActive = runIndex === indexLyric;
                    const takeUrls = takeUrlsFor(indexLyric);
                    const hasLocalTake = !!(sentenceRecordingsRef.current[indexLyric] && sentenceRecordingsRef.current[indexLyric].size);
                    const shownResult = lastResult && lastResult.position === indexLyric
                        ? lastResult
                        : (activeRec && activeRec.text !== 'skipped' ? { point: activeRec.point, text: activeRec.text } : null);
                    const micBusy = recordingAll || isScoringAll || savingTake
                        || ((recordingIndex > -1 || runIndex > -1) && recordingIndex !== indexLyric && runIndex !== indexLyric)
                        || micPermission === 'denied' || micPermission === 'unsupported';
                    const takeBtn = (key, src, title, subtitle) => {
                        const playing = playingTakeKey === key;
                        return (
                            <button key={key} onClick={() => toggleTake(key, src)}
                                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition-all ${playing ? 'bg-cyan-500/20 border border-cyan-500/40' : 'bg-white/5 border border-transparent hover:bg-white/10'}`}>
                                {playing ? <PauseCircleFilled className="text-2xl text-cyan-300" /> : <PlayCircleFilled className="text-2xl text-gray-300" />}
                                <span className="min-w-0">
                                    <span className="block text-white text-sm font-medium truncate">{title}</span>
                                    <span className="block text-gray-500 text-xs">{subtitle}</span>
                                </span>
                            </button>
                        );
                    };
                    return (
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                            {/* Left: the learner's saved recordings, newest first, to listen to again */}
                            <div className="lg:col-span-1 space-y-4 order-2 lg:order-1">
                                <div className="bg-[#1a2d4a] rounded-2xl p-5 shadow-xl border border-white/5">
                                    <h3 className="text-white font-semibold text-sm uppercase tracking-wider flex items-center gap-2">
                                        <ClockCircleOutlined className="text-cyan-400" /> Bản ghi của bạn
                                    </h3>
                                    <p className="text-gray-500 text-xs mt-1 mb-4">Câu {indexLyric + 1} · bấm để nghe lại</p>
                                    {savingTake && (
                                        <div className="flex items-center gap-2 text-cyan-300 text-xs mb-3">
                                            <LoadingOutlined spin /> Đang lưu bản ghi...
                                        </div>
                                    )}
                                    {takeUrls.length === 0 && !savingTake && (
                                        <p className="text-gray-500 text-sm">Chưa có bản ghi nào cho câu này.</p>
                                    )}
                                    <div className="space-y-2">
                                        {takeUrls.map((u, k) => takeBtn(`take-${u}`, proxyIfR2(u), formatTakeDate(takeDate(u)), k === 0 ? 'Mới nhất' : 'Lần ghi trước'))}
                                    </div>
                                    {isMergingRecording && (
                                        <div className="flex items-center gap-2 text-cyan-300 text-xs mt-3">
                                            <LoadingOutlined spin /> Đang ghép bản ghi cả bài...
                                        </div>
                                    )}
                                    {fullRecordingUrl && (
                                        <div className="mt-4 pt-4 border-t border-white/10">
                                            <p className="text-gray-400 text-xs uppercase tracking-wider mb-2">Cả bài</p>
                                            {takeBtn('full', proxyIfR2(fullRecordingUrl), formatTakeDate(takeDate(fullRecordingUrl)), 'Bản ghi cả bài')}
                                        </div>
                                    )}
                                </div>

                                {/* Tools */}
                                <div className="bg-[#1a2d4a] rounded-2xl p-5 shadow-xl border border-white/5">
                                    <h3 className="text-white font-semibold mb-4 text-sm uppercase tracking-wider">Công cụ</h3>
                                    <div className="grid grid-cols-2 gap-3">
                                        <Tooltip title="Chuyển sang trang bài học">
                                            <button
                                                onClick={() => navigate(`/lesson/listening/${slug}`, { state: { index } })}
                                                className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl transition-all text-sm font-medium w-full bg-blue-500/10 text-blue-400 hover:bg-blue-500/20"
                                            >
                                                <span className="text-lg">📚</span> Bài học
                                            </button>
                                        </Tooltip>
                                        <Tooltip title="Luyện nghe từng câu — nghe audio từng câu, có Prev/Next">
                                            <button
                                                onClick={() => navigate(`/lesson/listening/${slug}`, { state: { index, openDrillMode: true } })}
                                                className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl transition-all text-sm font-medium w-full bg-cyan-500/10 text-cyan-400 hover:bg-cyan-500/20"
                                            >
                                                <span className="text-lg">🎯</span> Luyện nghe
                                            </button>
                                        </Tooltip>
                                        <button onClick={() => setShowSettings(v => !v)}
                                            className={`col-span-2 flex items-center gap-2 px-4 py-3 rounded-xl transition-all text-sm font-medium w-full ${showSettings
                                                ? 'bg-cyan-500/20 text-cyan-300 hover:bg-cyan-500/30'
                                                : 'bg-white/5 text-gray-300 hover:bg-white/10'
                                                }`}>
                                            <SettingOutlined className="text-lg" /> Cài đặt
                                        </button>
                                    </div>
                                </div>

                                {/* Settings Panel */}
                                {showSettings && (
                                    <div className="relative z-60 bg-[#1a2d4a] rounded-2xl p-5 shadow-xl border border-white/5">
                                        <h3 className="text-white font-semibold mb-4 text-sm uppercase tracking-wider">Cài đặt</h3>
                                        <div className="space-y-4">
                                            <div>
                                                <p className="text-gray-400 text-xs mb-2 flex items-center gap-1">
                                                    <FontSizeOutlined /> Cỡ chữ phụ đề: {subtitleFontSize}px
                                                </p>
                                                <Slider min={12} max={28} value={subtitleFontSize} onChange={v => setSubtitleFontSize(v)}
                                                    className="[&_.ant-slider-track]:bg-blue-500 [&_.ant-slider-handle]:border-blue-500" />
                                            </div>
                                            <div>
                                                <p className="text-gray-400 text-xs mb-2 flex items-center gap-1">
                                                    <FontSizeOutlined /> Cỡ chữ bản dịch: {translationFontSize}px
                                                </p>
                                                <Slider min={10} max={24} value={translationFontSize} onChange={v => setTranslationFontSize(v)}
                                                    className="[&_.ant-slider-track]:bg-blue-500 [&_.ant-slider-handle]:border-blue-500" />
                                            </div>
                                            <div className="border-t border-white/10"></div>
                                            <div className="flex items-center justify-between">
                                                <p className="text-gray-400 text-xs flex items-center gap-1">
                                                    <EyeOutlined /> Hiển thị bản dịch
                                                </p>
                                                <Switch size="small" checked={showTranslation} onChange={v => setShowTranslation(v)}
                                                    className={showTranslation ? '!bg-blue-500' : ''} />
                                            </div>
                                            <div className="flex items-center justify-between">
                                                <p className="text-gray-400 text-xs flex items-center gap-1">
                                                    <EyeOutlined /> Hiển thị phiên âm IPA
                                                </p>
                                                <Switch size="small" checked={showIPA} onChange={v => setShowIPA(v)}
                                                    className={showIPA ? '!bg-blue-500' : ''} />
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Right: one sentence at a time */}
                            <div className="lg:col-span-2 space-y-4 order-1 lg:order-2">
                                <div className="bg-[#1a2d4a] rounded-2xl shadow-xl border border-white/5 overflow-hidden">
                                    <div className="flex items-center justify-between px-6 py-4 border-b border-white/5">
                                        <h3 className="text-white font-semibold flex items-center gap-2">
                                            <AudioOutlined className="text-blue-400" /> Luyện nói
                                        </h3>
                                        <span className="text-blue-300 text-sm bg-blue-500/10 px-3 py-1 rounded-full">
                                            Câu {totalLines === 0 ? 0 : indexLyric + 1}/{totalLines}
                                        </span>
                                    </div>
                                    <div className="px-6 py-8">
                                        <p className="text-white font-bold leading-snug text-center" style={{ fontSize: `${subtitleFontSize + 8}px` }}>
                                            {arrayMain[indexLyric] || <span className="opacity-40">…</span>}
                                        </p>
                                        {showIPA && (
                                            <p className="mt-3 text-center text-cyan-400/70 font-mono text-sm">{textToIPAPure(arrayMain[indexLyric] || '')}</p>
                                        )}
                                        {showTranslation && (
                                            <p className="mt-3 text-center text-yellow-300/70 italic" style={{ fontSize: `${translationFontSize}px` }}>
                                                {translationLoading ? <span className="opacity-40 animate-pulse">Đang dịch...</span> : (translations[indexLyric] || <span className="opacity-30">—</span>)}
                                            </p>
                                        )}
                                        {!isRunActive && shownResult && shownResult.text && (
                                            <p className="mt-5 text-center text-gray-400 text-sm">
                                                <span className="text-gray-500">Máy nghe được: </span>{shownResult.text}
                                            </p>
                                        )}
                                        {activeRec?.text === 'skipped' && (
                                            <p className="mt-5 text-center text-orange-400 text-xs">⚠️ Câu này đã được bỏ qua (khó/không hiểu)</p>
                                        )}
                                    </div>
                                </div>

                                {/* Score + actions: Re-record / Your take / Next */}
                                <div className="bg-[#1a2d4a] rounded-2xl p-5 shadow-xl border border-white/5">
                                    <div className="flex items-center gap-5">
                                        <div className="text-5xl font-extrabold text-cyan-300 tabular-nums min-w-[84px] text-center">
                                            {!isRunActive && shownResult ? shownResult.point : '--'}
                                        </div>
                                        <div>
                                            <p className="text-xs uppercase tracking-widest text-gray-400">Điểm</p>
                                            <p className="text-gray-300 text-sm">
                                                {isRunActive ? 'Đang ghi — điểm hiện ngay khi nói xong'
                                                    : savingTake ? 'Đang lưu bản ghi...'
                                                        : shownResult ? 'Đã chấm điểm câu này'
                                                            : 'Bấm Ghi âm để nói câu này'}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-2 gap-3 mt-5">
                                        <button
                                            disabled={micBusy}
                                            onClick={() => handlePerSentenceRecord(indexLyric)}
                                            className={`flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-bold transition-all ${micBusy ? 'bg-gray-600/60 text-gray-400 cursor-not-allowed' : 'bg-[#e8574d] text-white hover:bg-[#f0655b] shadow-lg shadow-[#e8574d]/20'}`}
                                        >
                                            <AudioOutlined />
                                            {recordingIndex === indexLyric ? 'Dừng & chấm điểm' : isRunActive ? 'Huỷ' : (shownResult || takeUrls.length > 0) ? 'Ghi lại' : 'Ghi âm'}
                                        </button>
                                        <button
                                            disabled={!hasLocalTake && takeUrls.length === 0}
                                            onClick={() => {
                                                if (playingTakeKey === 'mine') { stopTakePlayback(); return; }
                                                const blob = sentenceRecordingsRef.current[indexLyric];
                                                if (blob && blob.size) toggleTake('mine', URL.createObjectURL(blob));
                                                else toggleTake(`take-${takeUrls[0]}`, proxyIfR2(takeUrls[0]));
                                            }}
                                            className={`flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-bold transition-all ${(!hasLocalTake && takeUrls.length === 0) ? 'bg-white/5 text-gray-500 cursor-not-allowed' : 'bg-white/10 text-white hover:bg-white/20'}`}
                                        >
                                            {playingTakeKey === 'mine' ? <PauseCircleFilled /> : <PlayCircleFilled />} Nghe bản ghi của bạn
                                        </button>
                                    </div>
                                    <button
                                        disabled={indexLyric >= arrayMain.length - 1 || isRunActive}
                                        onClick={() => selectSentence(indexLyric + 1)}
                                        className={`w-full mt-3 px-4 py-3 rounded-xl text-sm font-bold transition-all ${(indexLyric >= arrayMain.length - 1 || isRunActive) ? 'bg-white/5 text-gray-500 cursor-not-allowed' : 'bg-blue-500 text-white hover:bg-blue-600 shadow-lg shadow-blue-500/20'}`}
                                    >
                                        Câu tiếp →
                                    </button>
                                    {!activeRec && !isRunActive && !savingTake && (
                                        <button
                                            onClick={() => skipSentence(indexLyric)}
                                            className="w-full mt-2 text-xs text-gray-500 hover:text-orange-400 transition-colors"
                                        >
                                            Bỏ qua câu này (khó/không hiểu)
                                        </button>
                                    )}
                                </div>

                                {/* Tips */}
                                <div className="bg-[#1a2d4a] rounded-2xl p-5 shadow-xl border border-white/5">
                                    <h3 className="text-white font-semibold mb-3 text-sm uppercase tracking-wider">
                                        <FireOutlined className="text-orange-400 mr-2" />Mẹo luyện nói
                                    </h3>
                                    <ul className="space-y-2 text-gray-300 text-sm">
                                        <li className="flex items-start gap-2">
                                            <CheckCircleOutlined className="text-green-400 mt-0.5 flex-shrink-0" />
                                            <span>Bấm <strong>Play</strong> ở thanh dưới để nghe mẫu của câu</span>
                                        </li>
                                        <li className="flex items-start gap-2">
                                            <CheckCircleOutlined className="text-green-400 mt-0.5 flex-shrink-0" />
                                            <span>Bấm <strong>Ghi âm</strong>: vạch vàng chạy tới sóng âm thì bắt đầu nói, hết sóng là xong câu</span>
                                        </li>
                                        <li className="flex items-start gap-2">
                                            <CheckCircleOutlined className="text-green-400 mt-0.5 flex-shrink-0" />
                                            <span>Điểm hiện ngay sau khi nói xong; bản ghi được lưu để nghe lại ở khung bên trái</span>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    );
                })()}
            </div>

            {/* Bottom Control Bar */}
            <div className="fixed bottom-0 left-0 right-0 bg-[#0a1628]/95 backdrop-blur-xl border-t border-white/10 z-30">
                {/* Sentence timeline: the reference waveform sits in the middle of the sentence's axis; pressing the mic sweeps
                    the yellow playhead from the start, recording begins where the waveform begins and ends where it ends */}
                {(() => {
                    const activeRec = recordMain.find(r => +r.position === indexLyric);
                    const isRecordingActive = recordingIndex === indexLyric;
                    const isRunActive = runIndex === indexLyric;
                    return (
                        <div className="max-w-screen-xl mx-auto px-3 pt-2">
                            <div className="bg-[#0f1e35] rounded-lg px-3 py-2 border border-cyan-500/20">
                                <div className="flex items-center justify-between mb-1.5">
                                    <span className="text-cyan-400 text-[11px] font-bold tracking-wider uppercase flex items-center gap-1.5">
                                        {isRunActive && <span className="w-1.5 h-1.5 bg-red-500 rounded-full animate-pulse" />}
                                        Giọng của bạn · {isRecordingActive ? 'Đang ghi' : isRunActive ? 'Chuẩn bị' : 'Sẵn sàng'}
                                    </span>
                                    <span className="text-gray-500 text-[11px] font-semibold uppercase tracking-wider">
                                        {isRunActive
                                            ? <span ref={elapsedLabelRef}>0.0s</span>
                                            : (activeRec && activeRec.text !== 'skipped' ? 'Đã ghi' : 'Chưa ghi')}
                                    </span>
                                </div>
                                <canvas ref={waveCanvasRef} width={600} height={64} className="w-full h-16 block" />
                            </div>
                        </div>
                    );
                })()}

                {/* Per-sentence timeline — same position and style as the Listening page's, but it spans just this sentence */}
                <div className="max-w-screen-xl mx-auto px-3 pt-2">
                    <div className="flex items-center gap-2">
                        <span ref={barElapsedRef} className="text-gray-400 text-xs font-mono w-10 text-right flex-shrink-0">0.0s</span>
                        <input
                            ref={barInputRef}
                            type="range"
                            min={0}
                            step={0.01}
                            disabled={runIndex > -1}
                            onChange={e => {
                                const sec = parseFloat(e.target.value);
                                const slot = slotRef.current;
                                if (!Number.isFinite(sec) || !slot || runRef.current.active) return;
                                const t = sec * 1000;
                                playheadMsRef.current = t;
                                const audioEl = audioElemRef.current;
                                if (audioEl && slot.startMs !== null) {
                                    const into = Math.min(Math.max(t - RUN_LEAD_MS, 0), slot.durMs);
                                    try { audioEl.currentTime = (slot.startMs + into) / 1000; } catch (err) { }
                                }
                                drawWaveform(t);
                                updateBarDom(t);
                            }}
                            style={{ accentColor: '#3b82f6', background: 'rgba(255,255,255,0.1)' }}
                            className="flex-1 h-1.5 rounded-lg appearance-none cursor-pointer"
                        />
                        <span ref={barTotalRef} className="text-gray-400 text-xs font-mono w-10 flex-shrink-0">0.0s</span>
                    </div>
                </div>
                <div className="max-w-screen-xl mx-auto px-4 py-3">
                    <div className="flex items-center justify-between gap-4">
                        {/* Track Info */}
                        <div className="hidden sm:block min-w-0 flex-shrink-0">
                            <p className="text-white text-sm font-semibold truncate max-w-[200px]">{dataLesson?.title}</p>
                            <p className="text-gray-400 text-xs">{story} • Câu {indexLyric + 1}/{totalLines}</p>
                            {recordingAll && (
                                <div className="flex gap-1 mt-1">
                                    {[0.5, 0.75, 1, 1.25, 1.5].map(s => (
                                        <button key={s}
                                            onClick={() => setReadingSpeed(s)}
                                            className={`px-1.5 py-0.5 rounded text-[10px] font-bold transition-all ${readingSpeed === s ? 'bg-blue-500 text-white' : 'bg-white/10 text-gray-400 hover:bg-white/20'
                                                }`}>
                                            {s}x
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Navigation + Record */}
                        <div className="flex items-center gap-2 sm:gap-4">
                            <button onClick={() => indexLyric > 0 && selectSentence(indexLyric - 1)}
                                className="text-gray-300 hover:text-white p-2 transition-colors">
                                <StepBackwardOutlined className="text-xl" />
                            </button>

                            {/* Play/pause the reference audio for the current sentence — independent of recording */}
                            <Tooltip title={audioIsPlaying ? 'Tạm dừng' : 'Nghe mẫu câu hiện tại'}>
                                <button
                                    disabled={runIndex > -1}
                                    onClick={() => {
                                        if (audioIsPlaying) {
                                            try { audioElemRef.current?.pause(); } catch (e) { }
                                            setAudioIsPlaying(false);
                                        } else {
                                            // continue from where the playhead was left on the timeline (0 = the sentence's start)
                                            playSentenceAudio(indexLyric, Math.max(0, playheadMsRef.current - RUN_LEAD_MS)).catch(() => { });
                                        }
                                    }}
                                    className={`w-10 h-10 rounded-full flex items-center justify-center bg-white/10 text-white transition-all ${runIndex > -1 ? 'opacity-40 cursor-not-allowed' : 'hover:bg-white/20'}`}
                                >
                                    {audioIsPlaying ? <PauseCircleFilled className="text-2xl" /> : <PlayCircleFilled className="text-2xl" />}
                                </button>
                            </Tooltip>

                            {/* Single mic button: record/score just the current sentence (replaces the old per-line mic buttons, and the old whole-lesson camera button) */}
                            <Tooltip title={
                                recordingIndex === indexLyric ? '🛑 Dừng ghi câu này & chấm điểm' :
                                    runIndex === indexLyric ? 'Đang chạy — bấm để huỷ' :
                                        recordingAll || isScoringAll ? 'Đang ghi toàn bài...' :
                                            recordingIndex > -1 || runIndex > -1 ? 'Đang ghi câu khác...' :
                                                micPermission === 'denied' ? 'Micro bị chặn — kiểm tra quyền trình duyệt' :
                                                    micPermission === 'unsupported' ? 'Trình duyệt không hỗ trợ — dùng Chrome/Edge' :
                                                        '🎤 Ghi âm câu hiện tại (vạch chạy tới sóng âm thì bắt đầu ghi)'
                            }>
                                <button
                                    disabled={recordingAll || isScoringAll || ((recordingIndex > -1 || runIndex > -1) && recordingIndex !== indexLyric && runIndex !== indexLyric) || micPermission === 'denied' || micPermission === 'unsupported'}
                                    onClick={() => handlePerSentenceRecord(indexLyric)}
                                    className={`w-12 h-12 rounded-full flex items-center justify-center shadow-lg transition-all hover:scale-105 ${recordingIndex === indexLyric || runIndex === indexLyric ? 'bg-red-500 shadow-red-500/30 animate-pulse'
                                        : (recordingAll || isScoringAll || ((recordingIndex > -1 || runIndex > -1) && recordingIndex !== indexLyric && runIndex !== indexLyric) || micPermission === 'denied' || micPermission === 'unsupported') ? 'bg-gray-600 shadow-gray-500/20 cursor-not-allowed'
                                            : 'bg-blue-500 shadow-blue-500/30'
                                        }`}>
                                    <AudioOutlined className="text-xl text-white" />
                                </button>
                            </Tooltip>

                            <button onClick={() => indexLyric < arrayMain.length - 1 && selectSentence(indexLyric + 1)}
                                className="text-gray-300 hover:text-white p-2 transition-colors">
                                <StepForwardOutlined className="text-xl" />
                            </button>
                        </div>

                        {/* Status */}
                        <div className="flex items-center gap-3">
                            {isScoringAll && (
                                <div className="hidden sm:flex items-center gap-2 bg-yellow-500/20 px-3 py-1.5 rounded-full">
                                    <LoadingOutlined className="text-yellow-400" spin />
                                    <span className="text-yellow-400 text-sm font-medium">Đang chấm điểm...</span>
                                </div>
                            )}
                            {listening && recordingAll && !isScoringAll && (
                                <div className="hidden sm:flex flex-col items-start gap-1 max-w-[200px]">
                                    <div className="flex items-center gap-2 bg-red-500/20 px-3 py-1 rounded-full">
                                        <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
                                        <span className="text-red-400 text-sm font-medium">Đang ghi toàn bài...</span>
                                    </div>
                                    {(transcript || interimTranscript) && (
                                        <p className="text-gray-400 text-xs truncate max-w-[200px] px-1">
                                            {(transcript + ' ' + interimTranscript).trim().slice(-60)}
                                        </p>
                                    )}
                                </div>
                            )}
                            {listening && !recordingAll && (
                                <div className="hidden sm:flex items-center gap-2 bg-red-500/20 px-3 py-1.5 rounded-full">
                                    <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
                                    <span className="text-red-400 text-sm font-medium">Đang nghe...</span>
                                </div>
                            )}
                            <span className={`relative z-10 text-sm font-bold px-3 py-1.5 rounded-full ${completionPercent >= 80 ? 'bg-green-500/20 text-green-400' :
                                completionPercent >= 50 ? 'bg-yellow-500/20 text-yellow-400' :
                                    'bg-blue-500/20 text-blue-400'
                                }`}>
                                {completionPercent}%
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
